/**
 * SingleChat Component
 *
 * Description: This component manages a chat interface where users can send and receive messages, including text and file attachments, with real-time updates. It also handles user selection, chat history fetching, and file upload functionalities.
 *
 * File Name: SingleChat.tsx
 * Date: 23-09-2024
 */

import React, {
  useState,
  useRef,
  useEffect,
  FormEvent,
  ChangeEvent,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComment,
  faEllipsisH,
  faEllipsisV,
  faGear,
  faMessage,
  faMicrophone,
  faPaperclip,
  faPaperPlane,
  faUserSlash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import { useNavigate, useParams } from "react-router-dom";
import qs from "qs";
import {
  IChatItem,
  IqueryParams,
  ISelectedChatUser,
} from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import moment from "moment";
import defaultProfile from "src/assets/images/default_profile_image.png";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@store/store";
import { CircularProgress } from "@mui/material";
import {
  resetSelectedChatUser,
  setSelectedChatUser,
  toogleChatLoader,
  setChatHistory,
  resetChatHistory,
  sendNewMessage,
  setChatList,
  resetIndividualCount,
  resetLastMessage,
} from "src/Store/chat";
import {
  convertToHyperlinks,
  downloadFile,
  ImageTypeValidate,
  truncateString,
} from "src/helpers/helperFunctions";
import { setCounter } from "src/Store/reducer";
import Audiocustom from "src/Components/Customer/Audiocustom";
import GradientBtn from "src/Components/WebsiteComponents/GradientBtn";
import ChatSkeleton from "src/Components/Loaders/ChatSkeleton";
interface IChatHistoryResponse {
  documents: IChatItem[];
  paginated: IPaginated;
}

const SingleChat: React.FC<{ isCustomerSide?: boolean; doctorID?: string }> = ({
  isCustomerSide = false,
  doctorID,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userDetails, messageUnreadCount } = useSelector(
    (store: RootState) => store.appReducer
  );
  const { selectedUser, chatHistory, latestChat } = useSelector(
    (store: RootState) => store.chat
  );
  // const { id = isCustomerSide ? doctorID : "" } = useParams();
  const id = doctorID;

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Full screen media viewer states
  const [fullScreenMedia, setFullScreenMedia] = useState<{
    url: string;
    type: string;
  } | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState<string>("");

  /**
   * Toggles the dropdown menu visibility.
   */
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  /**
   * Closes the dropdown menu.
   */
  const closeDropdown = () => {
    setIsOpen(false);
  };

  // ******************* SEND NEW MESSAGE ******************* //
  const [txtMsg, setTxtMsg] = useState<string>("");
  const [sendFiles, setSendFiles] = useState<File[]>([]);
  const [sendLoader, setSendLoader] = useState<boolean>(false);

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    let _filesList = e.target.files;
    let allFiles = Array.from(_filesList || []);
    let validFiles = allFiles.filter((file) =>
      ImageTypeValidate({
        file,
        allowedExtensions: [".jpg", ".jpeg", ".png", ".pdf", ".mp4", ".mov", ".avi", ".webm"],
        allowedTypes: [
          "image/png",
          "image/jpeg",
          "image/jpg",
          "application/pdf",
          "video/mp4",
          "video/quicktime",
          "video/x-msvideo",
          "video/webm",
        ],
        size: 5,
      })
    );
    setSendFiles(validFiles);
    e.target.value = "";
  };
  /**
   *API_CALL: Sends a text message to the selected user.
   */
  const sendTextMessage = async () => {
    const { res, err } = await httpRequest<{
      message: string;
      document: IChatItem;
    }>({
      method: "post",
      path: `/chat/message/send`,
      params: {
        receiverId: id,
        messageBody: txtMsg,
      },
    });
    if (res) {
      dispatch(sendNewMessage({ data: res.document, receiverId: id || "" }));
      setTxtMsg("");
    } else {
      toast(err, { type: "error" });
    }
  };

  /**
   *API_CALL: Sends selected files as messages to the selected user.
   */
  const sendFilesMessage = async () => {
    const formData = new FormData();
    sendFiles.forEach((file) => {
      formData.append("image", file);
    });
    formData.append("receiverId", id || "");
    const { res, err } = await httpRequest<{
      message: string;
      document: IChatItem[];
    }>({
      method: "post",
      path: `/chat/send/file`,
      params: formData,
      header: { "Content-Type": "multipart/form-data" },
    });
    if (res) {
      for (let i = 0; i < res.document.length; i++) {
        dispatch(
          sendNewMessage({ data: res.document[i], receiverId: id || "" })
        );
      }
      setSendFiles([]);
    } else {
      toast(err, { type: "error" });
    }
  };

  /**
   * Handles message sending, whether it's a text message or file message.
   */
  const sendMessage = async (event: FormEvent) => {
    event.preventDefault();
    if (sendLoader || (txtMsg.trim() === "" && sendFiles.length === 0)) return;
    setSendLoader(true);
    if (sendFiles.length > 0) {
      await sendFilesMessage();
    } else {
      await sendTextMessage();
    }
    setSendLoader(false);
  };
  // ******************* SEND NEW MESSAGE ******************* //

  // ******************* CHAT HISTORY ******************* //
  const userChatRef = useRef<HTMLDivElement | null>(null);
  const [fetchLoader, setFetchLoader] = useState<boolean>(false);
  const [queryParams, setQueryParams] = useState<IqueryParams>({
    page: 1,
    limit: 500,
  });
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination

  /**
   * Scrolls to the bottom of the chat history.
   */
  const scrollBottom = () => {
    if (userChatRef.current) {
      userChatRef.current.scrollTop = userChatRef.current.scrollHeight;
    }
  };

  /**
   *API_CALL: Fetches the chat history for the selected user.
   */
  const fetchChatHistory = async () => {
    setFetchLoader(true);
    const { res, err } = await httpRequest<IChatHistoryResponse>({
      path: `/chat/all/messages/${id}?${qs.stringify(queryParams)}`,
    });
    if (res) {
      let { documents = [], paginated } = res;
      dispatch(setChatHistory(documents.reverse())); // in future add pagination and sort
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" });
    }
    setFetchLoader(false);
  };

  useEffect(() => {
    scrollBottom();
  }, [chatHistory]);

  useEffect(() => {
    fetchChatHistory();

    return () => {
      dispatch(resetChatHistory());
    };
  }, [id, queryParams]);
  // ******************* CHAT HISTORY ******************* //

  // ******************* SET SELECTED CHAT USER ******************* //

  // ******************* DELETE CONVERSATION ******************* //
  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
  /**
   *API_CALL: Deletes the specified conversation from the database.
   */
  const handleDeleteConversation = async () => {
    setDeleteLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      method: "delete",
      path: `/chat/clear/${id}`,
      params: {},
    });
    if (res) {
      toast("Conversation deleted successfully", {
        type: "success",
      });
      dispatch(resetChatHistory());
      dispatch(resetLastMessage({ id: id || "" }));
      closeDropdown();
      //   navigate("/message");
    } else {
      toast(err, { type: "error" });
    }
    setDeleteLoader(false);
  };
  // ******************* DELETE CONVERSATION ******************* //

  // ******************* BLOCK STATES & FUNCTIONS ******************* //
  const [addBlockLoader, setAddBlockLoader] = useState<boolean>(false);
  /**
   *API_CALL: block the specified user from the conversation.
   */
  const blockUser = async () => {
    setAddBlockLoader(true);
    const { res, err } = await httpRequest<{ message: string }>({
      path: `/block/person/${id}`,
    });
    if (res) {
      const newArr = latestChat.filter((v) => v._id !== id);
      dispatch(setChatList(newArr));
      dispatch(
        setSelectedChatUser({
          ...selectedUser,
          isBlocked: selectedUser?.isBlocked === false ? true : false,
        } as ISelectedChatUser)
      );
      toast(
        `User ${
          selectedUser?.isBlocked === false ? "Blocked" : "Unblocked"
        } Successfully`,
        { type: "success" }
      );
      if (!isCustomerSide) {
        navigate("/message");
      }
    } else {
      toast(err, { type: "error" });
    }
    setAddBlockLoader(false);
  };
  // ******************* BLOCK STATES & FUNCTIONS ******************* //

  /**
   *API_CALL: Fetches the selected user's information.
   */
  const fetchSelectedUser = async () => {
    dispatch(toogleChatLoader(true));
    const { res, err } = await httpRequest<{
      message: string;
      documents: ISelectedChatUser[];
    }>({
      path: `/chat/profile/${id}`,
    });
    if (res) {
      let data = res.documents[0];
      console.log("Selected Chat User Data ====>", data);

      // data.userBlocked = data.userBlocked === true ? true : false;
      dispatch(setSelectedChatUser(data));
    } else {
      toast(err, { type: "error" });
    }
    dispatch(toogleChatLoader(false));
  };

  useEffect(() => {
    fetchSelectedUser();
    dispatch(resetIndividualCount({ id: id || "" }));
    dispatch(
      setCounter({
        key: "messageUnreadCount",
        val: messageUnreadCount
          ? messageUnreadCount -
            (latestChat.find((v) => v?._id === id)?.unRead || 0)
          : 0,
      })
    );

    return () => {
      dispatch(resetSelectedChatUser());
    };
  }, [id]);
  // ******************* SET SELECTED CHAT USER ******************* //

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const removeFiles = (_name: string) => {
    const updatedFiles = sendFiles.filter((file) => file.name !== _name);
    setSendFiles(updatedFiles);
  };
  const handleSubmit = (event: React.SyntheticEvent, file: File) => {
    console.log("Audio file submitted:", file);
    // You can also add any other dummy logic here
  };

  /**
   * Handles PDF download with loader
   */
  const handlePdfDownload = async (fileUrl: string) => {
    setDownloadingPdf(fileUrl);
    try {
      await downloadFile(fileUrl);
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setDownloadingPdf("");
    }
  };

  /**
   * Opens media in full screen
   */
  const openFullScreen = (url: string, type: string) => {
    setFullScreenMedia({ url, type });
  };

  /**
   * Closes full screen media viewer
   */
  const closeFullScreen = () => {
    setFullScreenMedia(null);
  };
  return selectedUser?.isBlocked === true ? (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center justify-center gap-2">
        <FontAwesomeIcon
          className="font-size-40px text-[#004540]"
          icon={faUserSlash}
        />
        <p className="font-Poppins-Regular font-size-16px">
          You have been blocked by the user
        </p>
        <GradientBtn
          ButtonText="Click here to Unblock"
          onPress={() => {
            blockUser();
          }}
          loader={addBlockLoader}
        />
      </div>
    </div>
  ) : (
    <>
      <section
        className={`chat-container-height ${
          isCustomerSide ? "bg-white" : "bg-[#EFEFEF]"
        }`}
      >
        <div className="flex justify-between items-center py-1.5 xl:py-2 px-2 xl:px-3 border-b border-[#E5E5E5] bg-white">
          <div
            className={`chat-header-inner-left ${
              fetchLoader ? "opacity-0" : "opacity-100"
            }`}
          >
            <div className="rounded-full w-8 h-8 lg:w-[2.5vw] lg:h-[2.5vw] relative">
              <img
                alt="Profile"
                className="rounded-full object-cover w-full h-full"
                src={`${
                  selectedUser?.photo ||
                  selectedUser?.coverPhoto ||
                  selectedUser?.profileImageUrl
                }`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement; // Assert the type
                  if (target) {
                    target.src = defaultProfile;
                  }
                }}
              />
              {selectedUser?.status && (
                <div className="absolute bottom-0.5 right-0 w-[0.6vw] h-[0.6vw] border border-[white] bg-green-500 rounded-full"></div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-Poppins-SemiBold font-size-15px capitalize">{`${
                  selectedUser?.firstName || ""
                } ${selectedUser?.lastName || ""}`}</h2>
                {/* <span className='text-[#00443F] font-Poppins-Regular font-size-12px'>typing...</span> */}
              </div>
              <p className="font-Poppins-regular font-size-13px">
                {selectedUser?.spcailization
                  ? truncateString(
                      selectedUser?.spcailization
                        ?.map((item: any) => item.name)
                        .join(" | ") || "Customer",
                      50
                    )
                  : "Customer"}
              </p>
            </div>
          </div>

          <div className="chat-header-inner-right relative">
            <div ref={dropdownRef}>
              <FontAwesomeIcon
                onClick={toggleDropdown}
                className="font-size-22px cursor-pointer text-[#707070]"
                icon={faEllipsisV}
              />
              {isOpen && (
                <div className="dropdown-box-header-chat min-w-[200px] w-fit">
                  <button
                    disabled={deleteLoader}
                    onClick={handleDeleteConversation}
                    className="font-Poppins-Medium whitespace-nowrap font-size-14px flex items-center gap-2 min-w-fit"
                  >
                    Delete Conversation{" "}
                    {deleteLoader && (
                      <CircularProgress
                        size={14}
                        style={{ color: "#004540" }}
                      />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className="chat-body chat-body-height custom-ai-scroll-bar"
          ref={userChatRef}
        >
          {fetchLoader ? (
            <ChatSkeleton count={6} />
          ) : chatHistory.length > 0 ? (
            <div className="absolute w-full p-2 lg:p-3">
              {chatHistory.map((item: IChatItem, index: number) => (
                <div
                  key={index}
                  className={`mb-4 ${
                    item.senderId === id
                      ? "chat-body-user-mess-left"
                      : "chat-body-user-mess-right"
                  }`}
                >
                  <img
                    alt="profile-picture"
                    src={`${
                      item.senderId === userDetails?._id
                        ? userDetails?.userRole === "doctor"
                          ? userDetails?.coverPhoto
                          : userDetails?.profileImageUrl
                        : selectedUser?.coverPhoto ||
                          selectedUser?.photo ||
                          selectedUser?.profileImageUrl ||
                          ""
                    }`}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target) {
                        target.src = defaultProfile;
                      }
                    }}
                    className="w-7 h-7 object-cover lg:w-8 lg:h-8 rounded-full"
                  />
                  <div className="chat-body-user-mess-inner">
                    <span
                      className={`font-Poppins-regular font-size-16px ${
                        item?.senderId === userDetails?._id
                          ? "text-black"
                          : "text-black"
                      }`}
                      style={{ wordBreak: "break-word" }}
                    >
                      {item.type === "text" ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: convertToHyperlinks(item?.messageBody),
                          }}
                        />
                      ) : item.type?.startsWith("image/") ? (
                        <img
                          src={item.fileUrl}
                          alt="file"
                          className="chat-file-img cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => openFullScreen(item.fileUrl, item.type || "")}
                        />
                      ) : item.type?.startsWith("audio/") ? (
                        <audio
                          controls
                          src={item.fileUrl}
                          className="chat-file-audio"
                        />
                      ) : item.type?.startsWith("video/") ? (
                        <div className="relative">
                          <video
                            controls
                            src={item.fileUrl}
                            className="chat-file-video cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              openFullScreen(item.fileUrl, item.type || "");
                            }}
                          />
                          <div className="absolute top-2 right-2 bg-black bg-opacity-50 rounded-full p-2 cursor-pointer hover:bg-opacity-70"
                            onClick={() => openFullScreen(item.fileUrl, item.type || "")}
                          >
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handlePdfDownload(item.fileUrl)}
                          disabled={downloadingPdf === item.fileUrl}
                          className="flex gap-2 items-center hover:opacity-80 transition-opacity"
                        >
                          <b>{item.messageBody}</b>
                          {downloadingPdf === item.fileUrl ? (
                            <CircularProgress
                              size={15}
                              style={{ color: "#004540" }}
                            />
                          ) : (
                            <svg
                              fill="none"
                              height={15}
                              width={15}
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{
                                display: "inline-block",
                                verticalAlign: "middle",
                              }}
                            >
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1={12} y1={15} x2={12} y2={3} />
                            </svg>
                          )}
                        </button>
                      )}
                    </span>
                    <span className="font-Poppins-regular date-msg">
                      {moment(item.createdAt).format("hh:mm a")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center h-full flex flex-col items-center justify-center gap-2">
              <div className="flex items-center justify-center bg-[#d8f1ef] rounded-full p-2 w-6 h-6 lg:w-[4.5vw] lg:h-[4.5vw]">
                <FontAwesomeIcon
                  className="font-size-40px text-[#a5e2dd]"
                  icon={faComment}
                />
              </div>
              <h2 className="font-Poppins-Medium font-size-18px">
                No messages yet!
              </h2>
              <h2 className="font-Poppins-Regular font-size-16px">
                Send a message to start the conversation
              </h2>
            </div>
          )}
        </div>

        {isCustomerSide ? (
          // Customer side UI - Image ke exact tarah ka design
          <div className="flex items-center relative w-full 2xl:gap-2 gap-1 px-2 2xl:py-2 py-1.5">
            {sendFiles.length ? (
              <div className="selected-files-main">
                {sendFiles.map((item: File, index: number) => (
                  <div key={index} className="selected-files-inner">
                    <span className="font-Poppins-regular font-size-13px">
                      {truncateString(item.name || "", 20)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFiles(item.name)}
                      className="cancel-file-button"
                    >
                      <FontAwesomeIcon icon={faXmark} className="text-[grey]" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="flex-1 flex items-center px-2 xl:px-3 py-1 xl:py-1.5 gap-1.5 relative bg-[#E9E9E9] border border-[#C7C7C7] rounded-full overflow-hidden">
              <input
                className="w-full bg-transparent font-Poppins-Regular font-size-16px border-none outline-none"
                type="text"
                readOnly={sendFiles.length ? true : false}
                value={sendFiles.length ? "" : txtMsg}
                onChange={(e) => setTxtMsg(e.target.value)}
                placeholder="Enter your message"
                required
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    sendMessage(e);
                  }
                }}
              />
              <label className="flex justify-center items-center cursor-pointer">
                <FontAwesomeIcon
                  className="text-[#707070] font-size-16px"
                  icon={faPaperclip}
                />
                <input
                  type="file"
                  id="fileInput"
                  style={{ display: "none" }}
                  multiple
                  onChange={onFileChange}
                />
              </label>
            </div>
            {txtMsg || sendFiles.length > 0 ? (
              <button
                disabled={sendLoader}
                onClick={(e) => {
                  if (txtMsg || sendFiles.length > 0) {
                    e.preventDefault();
                    sendMessage(e);
                  } else {
                  }
                }}
                className="flex justify-center items-center cursor-pointer theme-gradient-green-bottom rounded-full 2xl:w-10 2xl:h-10 w-7 h-7 xl:w-8 xl:h-8"
              >
                <span className={sendLoader ? "animate-pulse" : ""}>
                  <FontAwesomeIcon
                    className="text-white font-size-17px"
                    icon={sendLoader ? faEllipsisH : faPaperPlane}
                  />
                </span>
              </button>
            ) : (
              <Audiocustom receiverId={id || ""} customerUser />
            )}
          </div>
        ) : (
          // Original UI for non-customer side
          <div className="chat-footer w-full">
            <label
              htmlFor="fileInput"
              className="flex justify-center items-center cursor-pointer"
            >
              <FontAwesomeIcon
                className="font-size-26px text-[#707070]"
                icon={faPaperclip}
              />
            </label>
            <input
              type="file"
              id="fileInput"
              style={{ display: "none" }}
              multiple
              onChange={onFileChange}
            />
            <form
              onSubmit={sendMessage}
              className="chat-footer-message-sent-input relative"
            >
              {sendFiles.length ? (
                <div className="selected-files-main">
                  {sendFiles.map((item: File, index: number) => (
                    <div key={index} className="selected-files-inner">
                      <span className="font-Poppins-regular font-size-13px">
                        {truncateString(item.name || "", 20)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFiles(item.name)}
                        className="cancel-file-button"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              <input
                className="font-Poppins-Medium font-size-16px"
                type="text"
                readOnly={sendFiles.length ? true : false}
                value={
                  sendFiles.length
                    ? sendFiles.map((v) => truncateString(v.name || "", 20))
                    : txtMsg
                }
                onChange={(e) => setTxtMsg(e.target.value)}
                placeholder="Type Message..."
                required
              />
              <button
                disabled={sendLoader}
                type="submit"
                className="message-sent"
              >
                <span className={sendLoader ? "animate-pulse" : ""}>
                  <FontAwesomeIcon
                    className="text-white font-size-22px"
                    icon={sendLoader ? faEllipsisH : faPaperPlane}
                  />
                </span>
              </button>
            </form>
            <Audiocustom receiverId={id || ""} />
          </div>
        )}
      </section>

      {/* Full Screen Media Viewer Modal */}
      {fullScreenMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
          onClick={closeFullScreen}
        >
          <button
            onClick={closeFullScreen}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-[10000]"
          >
            <FontAwesomeIcon icon={faXmark} className="text-3xl" />
          </button>
          <div
            className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {fullScreenMedia.type?.startsWith("image/") ? (
              <img
                src={fullScreenMedia.url}
                alt="Full screen"
                className="max-w-full max-h-[90vh] object-contain"
              />
            ) : fullScreenMedia.type?.startsWith("video/") ? (
              <video
                controls
                autoPlay
                src={fullScreenMedia.url}
                className="max-w-full max-h-[90vh]"
              />
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};

export default SingleChat;
